import type { Nullable, RGBA } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { ImageRectComponent } from "../image-rect-component";
import { CanvasResizeSelectionBottomComponent } from "./canvas-resize-selection-bottom-component";
import { CanvasResizeSelectionBottomLeftComponent } from "./canvas-resize-selection-bottom-left-component";
import { CanvasResizeSelectionBottomRightComponent } from "./canvas-resize-selection-bottom-right-component";
import CanvasLineUnitComponent from "./canvas-line-unit-component";
import { CanvasResizeSelectionLeftComponent } from "./canvas-resize-selection-left-component";
import { CanvasResizeSelectionRightComponent } from "./canvas-resize-selection-right-component";
import { CanvasResizeSelectionTopComponent } from "./canvas-resize-selection-top-component";
import { CanvasResizeSelectionTopLeftComponent } from "./canvas-resize-selection-top-left-component";
import { CanvasResizeSelectionTopRightComponent } from "./canvas-resize-selection-top-right-component";
import CanvasResizeUnitComponent from "./canvas-resize-unit-component";

const CANVAS_RESIZE_CENTER_SNAP_DISTANCE_PX = 10;
const CANVAS_RESIZE_CENTER_SNAP_RELEASE_DISTANCE_PX = 16;
const CANVAS_RESIZE_CENTER_GUIDE_THICKNESS_PX = 2;
const CANVAS_RESIZE_CENTER_CROSSHAIR_LENGTH_PX = 22;
const CANVAS_RESIZE_CENTER_CROSSHAIR_OUTER_THICKNESS_PX = 3;
const CANVAS_RESIZE_CENTER_CROSSHAIR_INNER_THICKNESS_PX = 1;
// Keep the same accent color as selection/crop UI (see selection-grid shader).
const CANVAS_RESIZE_SELECTION_ACCENT_COLOR: RGBA = [0, 0.6, 0.63, 1];
const CANVAS_RESIZE_CENTER_CROSSHAIR_OUTER_COLOR: RGBA = [0, 0.6, 0.63, 0.35];
const CANVAS_RESIZE_CENTER_CROSSHAIR_INNER_COLOR: RGBA = CANVAS_RESIZE_SELECTION_ACCENT_COLOR;

export class CanvasResizeSelectionComponent extends Webgl2Component {
  private _prevTranslation = Vec2.zero();
  private _mouseDownPosition = Vec2.zero();

  private _targetCenterVGuide: Nullable<CanvasLineUnitComponent> = null;
  private _targetCenterHGuide: Nullable<CanvasLineUnitComponent> = null;
  private _isSnappedToTargetCenterX = false;
  private _isSnappedToTargetCenterY = false;

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();

    const topLeftComponent = new CanvasResizeSelectionTopLeftComponent(this._renderer, this);
    topLeftComponent.style.top = "0";
    topLeftComponent.style.left = "0";
    topLeftComponent.style.translateX = "-2px";
    topLeftComponent.style.translateY = "-2px";
    this.appendChild(topLeftComponent);

    const topComponent = new CanvasResizeSelectionTopComponent(this._renderer, this);
    topComponent.style.top = "0";
    topComponent.style.left = "50%";
    topComponent.style.translateX = "-50%";
    topComponent.style.translateY = "-50%";
    this.appendChild(topComponent);

    const topRightComponent = new CanvasResizeSelectionTopRightComponent(this._renderer, this);
    topRightComponent.style.top = "0";
    topRightComponent.style.left = "100%";
    topRightComponent.style.translateX = "-100%&2px";
    topRightComponent.style.translateY = "-2px";
    this.appendChild(topRightComponent);

    const leftComponent = new CanvasResizeSelectionLeftComponent(this._renderer, this);
    leftComponent.style.top = "50%";
    leftComponent.style.left = "0";
    leftComponent.style.translateX = "-50%";
    leftComponent.style.translateY = "-50%";
    this.appendChild(leftComponent);

    const rightComponent = new CanvasResizeSelectionRightComponent(this._renderer, this);
    rightComponent.style.top = "50%";
    rightComponent.style.left = "100%";
    rightComponent.style.translateX = "-50%";
    rightComponent.style.translateY = "-50%";
    this.appendChild(rightComponent);

    const bottomLeftComponent = new CanvasResizeSelectionBottomLeftComponent(this._renderer, this);
    bottomLeftComponent.style.top = "100%";
    bottomLeftComponent.style.left = "0";
    bottomLeftComponent.style.translateX = "-2px";
    bottomLeftComponent.style.translateY = "-100%&2px";
    this.appendChild(bottomLeftComponent);

    const bottomComponent = new CanvasResizeSelectionBottomComponent(this._renderer, this);
    bottomComponent.style.top = "100%";
    bottomComponent.style.left = "50%";
    bottomComponent.style.translateX = "-50%";
    bottomComponent.style.translateY = "-50%";
    this.appendChild(bottomComponent);

    const bottomRightComponent = new CanvasResizeSelectionBottomRightComponent(
      this._renderer,
      this,
    );
    bottomRightComponent.style.top = "100%";
    bottomRightComponent.style.left = "100%";
    bottomRightComponent.style.translateX = "-100%&2px";
    bottomRightComponent.style.translateY = "-100%&2px";
    this.appendChild(bottomRightComponent);

    this._appendCenterCrosshair();

    // TODO:?
    // NOTE: This is crutch to update component on scale change, since component doesn't have
    // relation to camera change
    this._renderer.camera.events.on("scale", (newScale: number) => {
      const value = 1 / newScale;
      this.forEachChildren((child) => {
        child.style.scaleX = value * window.devicePixelRatio;
        child.style.scaleY = value * window.devicePixelRatio;
      });
    });
  }

  private _appendCenterCrosshair() {
    const centerBaseStyles = (component: Webgl2Component) => {
      component.style.top = "50%";
      component.style.left = "50%";
      component.style.translateX = "-50%";
      component.style.translateY = "-50%";
      component.style.pointerEvents = "none";
    };

    const vOuter = new CanvasResizeUnitComponent(
      new Vec2(
        CANVAS_RESIZE_CENTER_CROSSHAIR_OUTER_THICKNESS_PX,
        CANVAS_RESIZE_CENTER_CROSSHAIR_LENGTH_PX,
      ),
      CANVAS_RESIZE_CENTER_CROSSHAIR_OUTER_COLOR,
    );
    centerBaseStyles(vOuter);
    this.appendChild(vOuter);

    const vInner = new CanvasResizeUnitComponent(
      new Vec2(
        CANVAS_RESIZE_CENTER_CROSSHAIR_INNER_THICKNESS_PX,
        CANVAS_RESIZE_CENTER_CROSSHAIR_LENGTH_PX,
      ),
      CANVAS_RESIZE_CENTER_CROSSHAIR_INNER_COLOR,
    );
    centerBaseStyles(vInner);
    this.appendChild(vInner);

    const hOuter = new CanvasResizeUnitComponent(
      new Vec2(
        CANVAS_RESIZE_CENTER_CROSSHAIR_LENGTH_PX,
        CANVAS_RESIZE_CENTER_CROSSHAIR_OUTER_THICKNESS_PX,
      ),
      CANVAS_RESIZE_CENTER_CROSSHAIR_OUTER_COLOR,
    );
    centerBaseStyles(hOuter);
    this.appendChild(hOuter);

    const hInner = new CanvasResizeUnitComponent(
      new Vec2(
        CANVAS_RESIZE_CENTER_CROSSHAIR_LENGTH_PX,
        CANVAS_RESIZE_CENTER_CROSSHAIR_INNER_THICKNESS_PX,
      ),
      CANVAS_RESIZE_CENTER_CROSSHAIR_INNER_COLOR,
    );
    centerBaseStyles(hInner);
    this.appendChild(hInner);
  }

  public copyComponentTransform(component: Nullable<Webgl2Component<any, any>>) {
    if (!component) return;
    this.transform.copy(component.transform);
  }

  public onMouseDown(e: MouseEvent | TouchEvent) {
    this._mouseDownPosition = this._renderer.camera.getGlobalMousePixelPosition(e);
    this._prevTranslation = this._renderer.canvasTranslation.clone();
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    const currentMousePos = this._renderer.camera.getGlobalMousePixelPosition(e);
    const diff = currentMousePos.sub(this._mouseDownPosition);
    let newTranslation = diff.add(this._prevTranslation);

    const targetCenter = this._getSnapTargetCenter();
    if (!targetCenter) {
      this._isSnappedToTargetCenterX = false;
      this._isSnappedToTargetCenterY = false;
      this._hideTargetCenterGuides();
      this._renderer.setCanvasTranslate(newTranslation);
      return;
    }

    const canvasHalfSize = this._renderer.canvasSize.clone().divide(2);
    const nextCanvasCenter = newTranslation.clone().add(canvasHalfSize);

    const scale = this._renderer.camera.scale || 1;
    const snapIn = CANVAS_RESIZE_CENTER_SNAP_DISTANCE_PX / scale;
    const snapOut = CANVAS_RESIZE_CENTER_SNAP_RELEASE_DISTANCE_PX / scale;

    const dx = Math.abs(nextCanvasCenter.x - targetCenter.x);
    const dy = Math.abs(nextCanvasCenter.y - targetCenter.y);

    const shouldSnapX = this._isSnappedToTargetCenterX ? dx <= snapOut : dx <= snapIn;
    const shouldSnapY = this._isSnappedToTargetCenterY ? dy <= snapOut : dy <= snapIn;

    this._isSnappedToTargetCenterX = shouldSnapX;
    this._isSnappedToTargetCenterY = shouldSnapY;

    if (shouldSnapX) newTranslation.setX(targetCenter.x - canvasHalfSize.x);
    if (shouldSnapY) newTranslation.setY(targetCenter.y - canvasHalfSize.y);

    this._renderer.setCanvasTranslate(newTranslation);
    this._updateTargetCenterGuides(targetCenter, shouldSnapX, shouldSnapY);
  }

  public onMouseUp(e: MouseEvent | TouchEvent) {
    void e;
    this._isSnappedToTargetCenterX = false;
    this._isSnappedToTargetCenterY = false;
    this._hideTargetCenterGuides();
  }

  private _getSnapTargetCenter(): Nullable<Vec2> {
    let target: ImageRectComponent | ImageAdjustRectComponent | null = null;
    for (const component of this._renderer.components) {
      if (
        (component instanceof ImageRectComponent ||
          component instanceof ImageAdjustRectComponent) &&
        component.style.display !== "none"
      ) {
        target = component;
      }
    }

    if (!target) return null;

    return Webgl2dRenderer.global.getComponentClipSpaceInGlobal(target, Vec2.half());
  }

  private _hideTargetCenterGuides() {
    if (this._targetCenterVGuide) this._targetCenterVGuide.style.display = "none";
    if (this._targetCenterHGuide) this._targetCenterHGuide.style.display = "none";
  }

  private _updateTargetCenterGuides(targetCenter: Vec2, showV: boolean, showH: boolean) {
    const scale = this._renderer.camera.scale || 1;
    const thickness = CANVAS_RESIZE_CENTER_GUIDE_THICKNESS_PX / scale;

    const canvasTranslation = this._renderer.canvasTranslation.clone();
    const canvasSize = this._renderer.canvasSize.clone();
    const canvasCenter = canvasTranslation.clone().add(canvasSize.clone().divide(2));

    if (showV) {
      const size = new Vec2(thickness, canvasSize.height);
      if (!this._targetCenterVGuide) {
        this._targetCenterVGuide = new CanvasLineUnitComponent(size);
        this._targetCenterVGuide.style.display = "none";
        this._renderer.addComponent(this._targetCenterVGuide);
      }
      this._targetCenterVGuide.transform.calcDimensions({
        position: new Vec2(targetCenter.x, canvasCenter.y),
        size,
        rotation: 0,
        roundSize: false,
      });
      this._targetCenterVGuide.style.display = "initial";
    } else if (this._targetCenterVGuide) {
      this._targetCenterVGuide.style.display = "none";
    }

    if (showH) {
      const size = new Vec2(canvasSize.width, thickness);
      if (!this._targetCenterHGuide) {
        this._targetCenterHGuide = new CanvasLineUnitComponent(size);
        this._targetCenterHGuide.style.display = "none";
        this._renderer.addComponent(this._targetCenterHGuide);
      }
      this._targetCenterHGuide.transform.calcDimensions({
        position: new Vec2(canvasCenter.x, targetCenter.y),
        size,
        rotation: 0,
        roundSize: false,
      });
      this._targetCenterHGuide.style.display = "initial";
    } else if (this._targetCenterHGuide) {
      this._targetCenterHGuide.style.display = "none";
    }
  }

  public getRenderData() {
    if (!this._renderer.hasSelectedComponent) return;

    const material = Webgl2.materials.selectionGrid;
    material.setUniforms({
      u_size: this.transform.size.getAbs().getArray(),
      u_showGrid: true,
    });
    return { material };
  }
}

export default CanvasResizeSelectionComponent;
